import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import FileInputForm, {
  CreateDocumentData,
  UpdateDocumentData,
} from '@/shared/components/ui/upload/file-input-form';
import { ProjectDocument } from '@/shared/types/api';

export const VisbilityOptions = ['public', 'private', 'internal'] as const;
export type DocumentVisibility = (typeof VisbilityOptions)[number];

export const useVisbilityFromString = () => {
  const { t } = useTranslation('document', { keyPrefix: 'DocumentHub' });
  const translateVisibility = (visibility: DocumentVisibility) => {
    switch (visibility) {
      case 'public':
        return t('document_visibility_public');
      case 'private':
        return t('document_visibility_private');
      case 'internal':
        return t('document_visibility_internal');
      default:
        return t('document_visibility_public');
    }
  };
  return { translateVisibility };
};

export type FileDetails = {
  file: File;
  title: string;
  project: number;
  partner?: string | null;
  kpi?: string | null;
  type: string;
  visibility: DocumentVisibility;
  owner: string;
  date: Date;
  association?: string | null;
};

export type DocumentData =
  | ProjectDocument
  | ({
      id?: number;
      tempId?: string;
      title: string;
      project_id: number;
      type?: string;
      visibility?: DocumentVisibility;
      association?: string;
      file_path?: string;
      creator_id?: string;
      org_id?: number;
    } & {
      file: File | null;
    });

type DocumentDialogProps = {
  editMode?: boolean;
  isOpen?: boolean;
  publicOnly?: boolean;
  document?: DocumentData;
  projectId?: number;
  onClose: () => void;
  onSubmitSuccess: (data: ProjectDocument) => void;
  onCustomSubmit?: (data: CreateDocumentData) => void;
  onCustomEdit?: (data: UpdateDocumentData) => void;
};

export const DocumentUploadDialog = ({
  editMode = false,
  document,
  isOpen = false,
  onClose,
  projectId,
  onSubmitSuccess,
  publicOnly = false,
  onCustomSubmit,
  onCustomEdit,
}: DocumentDialogProps) => {
  const { t } = useTranslation('document', { keyPrefix: 'DocumentHub' });
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (document) {
      setFile('file' in document ? document.file : null);
    }
  }, [document]);

  const resetDialog = () => {
    setFile(null);
    onClose();
  };
  const handleFileChange = (newFile: File | null) => {
    setFile(newFile);
  };

  const title = editMode
    ? t('document_upload_dialog.edit_title')
    : t('document_upload_dialog.upload_title');

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          resetDialog();
        }
      }}
    >
      <DialogContent closeInline={false}>
        <DialogHeader className="pb-4">
          <DialogTitle className="text-[32px] font-[600]">
            {title ? title : t('document_upload_dialog.upload_title')}
          </DialogTitle>
        </DialogHeader>

        <FileInputForm
          onCloseDialog={resetDialog}
          file={file}
          document={document}
          editMode={editMode}
          projectId={projectId}
          onSubmitSuccess={onSubmitSuccess}
          handleFileChange={handleFileChange}
          publicOnly={publicOnly}
          onCustomSubmit={onCustomSubmit}
          onCustomEdit={onCustomEdit}
        />
      </DialogContent>
    </Dialog>
  );
};
